import React, { useEffect, useState } from "react";
import { LeaveService } from "../../../services/leave";

interface LeaveBalance {
    empId: string;
    name: string;
    department: string;
    [key: string]: any; // 👈 dynamic leave types
}

const LeaveBalanceTab: React.FC = () => {
    const [data, setData] = useState<LeaveBalance[]>([]);
    const [leaveTypes, setLeaveTypes] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const { getLeaveBalanceExcelFormat, getLeaveBalanceList, uploadLeaveBalanceExcel } =
        LeaveService();

    // ✅ Fetch Data From Backend
    const fetchLeaveBalance = async () => {
        try {
            setLoading(true);
            const response = await getLeaveBalanceList();

            const result = response.data; // 👈 array directly

            setData(result);

            // ✅ Extract dynamic leave types from result
            const leaveTypeSet = new Set<string>();

            result.forEach((item: any) => {
                Object.keys(item).forEach((key) => {
                    if (
                        key !== "empId" &&
                        key !== "name" &&
                        key !== "department"
                    ) {
                        leaveTypeSet.add(key);
                    }
                });
            });

            setLeaveTypes(Array.from(leaveTypeSet));

        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaveBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Dynamic Total Calculation
    const calculateTotal = (row: LeaveBalance) => {
        return leaveTypes.reduce((sum, type) => {
            return sum + (Number(row[type]) || 0);
        }, 0);
    };

    // ✅ Download Excel From Backend
    const handleDownload = async () => {
        try {
            const response = await getLeaveBalanceExcelFormat();

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = "Leave_Balance_Report.xlsx";
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download error:", error);
            alert("Failed to download file");
        }
    };
    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            setLoading(true);
            await uploadLeaveBalanceExcel(formData);
            alert("Leave balance updated successfully");
            fetchLeaveBalance(); // refresh table
        } catch (error) {
            console.error("Upload error:", error);
            alert("Upload failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex justify-center items-start min-h-[80vh] bg-gray-100 py-4 sm:py-8 px-2 sm:px-4 overflow-auto">
            <div className="relative w-full max-w-7xl bg-white rounded-xl shadow-lg p-2 sm:p-6 mx-auto overflow-auto">
                <div className="min-w-[800px]">
                    <h1 className="text-2xl font-semibold">
                        Leave Balance
                    </h1>

                    <div className="flex gap-3">
                        <button
                            onClick={handleDownload}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                            Download Excel
                        </button>

                        <label className="px-4 py-2 bg-green-600 text-white rounded-lg cursor-pointer hover:bg-green-700 transition">
                            Upload Excel
                            <input
                                type="file"
                                accept=".xlsx"
                                onChange={handleUpload}
                                className="hidden"
                            />
                        </label>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-10">Loading...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-200 rounded-lg">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="p-3 text-left">Emp ID</th>
                                    <th className="p-3 text-left">Name</th>
                                    <th className="p-3 text-left">Department</th>

                                    {/* ✅ Dynamic Leave Headers */}
                                    {leaveTypes?.map((type) => (
                                        <th
                                            key={type}
                                            className="p-3 text-center"
                                        >
                                            {type}
                                        </th>
                                    ))}

                                    <th className="p-3 text-center">Total</th>
                                </tr>
                            </thead>

                            <tbody>
                                {data.map((row, index) => (
                                    <tr key={index} className="border-t">
                                        <td className="p-3">{row.empId}</td>
                                        <td className="p-3">{row.name}</td>
                                        <td className="p-3">{row.department}</td>

                                        {/* ✅ Dynamic Leave Values */}
                                        {leaveTypes?.map((type) => (
                                            <td
                                                key={type}
                                                className="p-3 text-center"
                                            >
                                                {row[type] || 0}
                                            </td>
                                        ))}

                                        <td className="p-3 text-center font-semibold">
                                            {calculateTotal(row)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && data.length === 0 && (
                    <div className="text-center text-gray-500 mt-6">
                        No leave balance data available.
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaveBalanceTab;